import { $ } from '../common/dom.ts';

const TOKEN_KEY = 'pocket_token';

/**
 * Jeton de session. Celui du Guest (dev local) reste dans l'onglet (sessionStorage) : les autres onglets gardent
 * le compte principal, ce qui permet de jouer à deux depuis le même navigateur.
 */
export const sessionStore = {
  get: (): string | null => sessionStorage.getItem(TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY),
  set: (token: string, tabOnly = false): void => (tabOnly ? sessionStorage : localStorage).setItem(TOKEN_KEY, token),
  clear: (): void => {
    if (sessionStorage.getItem(TOKEN_KEY) !== null) sessionStorage.removeItem(TOKEN_KEY);
    else localStorage.removeItem(TOKEN_KEY);
  },
};

/** Le peu de Google Identity Services (https://accounts.google.com/gsi/client) dont on se sert. */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(options: { client_id: string; callback: (response: { credential: string }) => void; ux_mode?: 'popup' }): void;
      renderButton(parent: HTMLElement, options: Record<string, string | number>): void;
      disableAutoSelect(): void;
    };
  };
}

declare global {
  interface Window { google?: GoogleIdentityServices }
}

type GoogleReply = { token: string } | { needsUsername: true; suggestion: string };

/** Attend le chargement du script Google (il peut être bloqué par une extension). */
function loadGoogle(): Promise<GoogleIdentityServices> {
  return new Promise((resolve, reject) => {
    if (window.google) return resolve(window.google);
    const script = document.querySelector<HTMLScriptElement>('script[src*="accounts.google.com/gsi/client"]');
    if (!script) return reject(new Error('Script Google absent.'));
    script.addEventListener('load', () => (window.google ? resolve(window.google) : reject(new Error('Google indisponible.'))));
    script.addEventListener('error', () => reject(new Error('Google indisponible.')));
  });
}

/** Connexion avec Google ; un nouveau compte choisit d'abord son pseudo. */
export class AuthScreen {
  private readonly screen = $('#auth');
  private readonly googleSlot = $('#auth-google');
  private readonly guestButton = $<HTMLButtonElement>('#auth-guest');
  private readonly newAccount = $('#auth-new');
  private readonly signupForm = $<HTMLFormElement>('#auth-signup');
  private readonly error = $('#auth-error');
  private readonly onToken: (token: string) => void;
  /** Jeton Google en attente du choix d'un pseudo (valable une heure). */
  private credential: string | null = null;
  private ready: Promise<void> | null = null;

  constructor(onToken: (token: string) => void) {
    this.onToken = onToken;
    this.signupForm.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.send({ username: new FormData(this.signupForm).get('username') });
    });
    $('#auth-cancel').addEventListener('click', () => this.backToGoogle());
    this.guestButton.addEventListener('click', () => void this.loginAsGuest());
  }

  show(message = ''): void {
    this.screen.hidden = false;
    this.error.textContent = message;
    this.ready ??= this.setUpGoogle().catch((error: unknown) => {
      this.ready = null;
      this.googleSlot.classList.add('is-disabled');
      this.error.textContent = error instanceof Error ? error.message : String(error);
    });
  }

  hide(): void {
    this.screen.hidden = true;
  }

  private async setUpGoogle(): Promise<void> {
    this.googleSlot.classList.remove('is-disabled');
    this.googleSlot.classList.add('is-loading');
    const response = await fetch('/api/auth/config');
    const { googleClientId, guest } = (await response.json()) as { googleClientId: string | null; guest: boolean };
    this.guestButton.hidden = !guest;
    if (!googleClientId) throw new Error('La connexion Google n\'est pas configurée sur ce serveur.');
    const google = await loadGoogle().catch(() => {
      throw new Error('Impossible de charger la connexion Google (bloqueur de publicités ?).');
    });
    google.accounts.id.initialize({
      client_id: googleClientId,
      ux_mode: 'popup',
      callback: ({ credential }) => {
        this.credential = credential;
        void this.send({});
      },
    });
    // Le bouton Google (une iframe qu'on ne peut pas styliser) est rendu invisible par-dessus notre propre bouton.
    this.googleSlot.querySelector('.google-host')?.remove();
    const host = document.createElement('div');
    host.className = 'google-host';
    this.googleSlot.append(host);
    const width = Math.min(400, Math.max(200, Math.round(this.googleSlot.clientWidth || 300)));
    google.accounts.id.renderButton(host, { theme: 'outline', size: 'large', shape: 'pill', text: 'continue_with', locale: 'fr', width });
    this.googleSlot.classList.remove('is-loading');
  }

  private backToGoogle(): void {
    this.credential = null;
    this.newAccount.hidden = true;
    this.googleSlot.hidden = false;
  }

  /** Dev local : connecte toujours le même compte Guest, pour un second joueur. */
  private async loginAsGuest(): Promise<void> {
    this.error.textContent = '';
    try {
      const response = await fetch('/api/guest', { method: 'POST' });
      const data = (await response.json()) as { token?: string; error?: string };
      if (!response.ok || !data.token) throw new Error(data.error ?? 'Connexion impossible.');
      sessionStore.set(data.token, true);
      this.onToken(data.token);
    } catch (error) {
      this.error.textContent = error instanceof Error ? error.message : String(error);
    }
  }

  private async send(extra: Record<string, unknown>): Promise<void> {
    if (!this.credential) return this.backToGoogle();
    this.error.textContent = '';
    try {
      const response = await fetch('/api/google', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ credential: this.credential, ...extra }),
      });
      const data = (await response.json()) as GoogleReply & { error?: string };
      if (response.status === 401) this.backToGoogle(); // jeton Google expiré ou refusé
      if (!response.ok) throw new Error(data.error ?? 'Connexion impossible.');
      if ('needsUsername' in data) {
        this.googleSlot.hidden = true;
        this.newAccount.hidden = false;
        const input = $<HTMLInputElement>('[name="username"]', this.signupForm);
        if (!input.value) input.value = data.suggestion;
        input.focus();
        return;
      }
      this.backToGoogle();
      sessionStore.set(data.token);
      this.onToken(data.token);
    } catch (error) {
      this.error.textContent = error instanceof Error ? error.message : String(error);
    }
  }
}

export async function logout(token: string): Promise<void> {
  await fetch('/api/logout', { method: 'POST', headers: { authorization: `Bearer ${token}` } }).catch(() => undefined);
  window.google?.accounts.id.disableAutoSelect();
  sessionStore.clear();
}
